import {isEqual} from 'lodash';
import debounceWithResolver from '../debounceWithResolver';
import createAsyncMutex from '../createAsyncMutex';
import {AddSavePromise} from '../../hooks/useIsSaved';
import createRemoteId, {RemoteId} from './createRemoteId';

const debouncedFetch = debounceWithResolver(window.fetch, 500);

const savedAreas: Record<string, Area> = {};
const remoteIds: Record<string, RemoteId> = {};

export default createAsyncMutex(async (
    area: Area,
    addSavePromise: AddSavePromise,
): Promise<string> => {
    const {areaLocalId} = area.properties;
    // Populate area's remoteId if it doesn't yet exist
    getAreaRemoteId(area);
    const remoteId = remoteIds[area.properties.areaLocalId];

    const isModifiedSinceSave = getIsModifiedSinceSave(area);
    savedAreas[areaLocalId] = area;
    if (!isModifiedSinceSave || !area.isGeometryLoaded) return remoteId.promise;

    const body = JSON.stringify({
        type: 'Feature',
        properties: {
            ...area.properties,
            hexcolor: area.properties.color,
        },
        geometry: area.unmodifiedArea.geometry,
    });

    if (area.properties.isLocallyDeleted) {
        if (remoteId.sync) {
            window.fetch(`/api/objects/area/${remoteId.sync}/`, {
                credentials: 'same-origin',
                method: 'DELETE',
            });
        }
        delete remoteIds[area.properties.areaLocalId];

    } else if (remoteId.sync) {
        addSavePromise(debouncedFetch(`/api/objects/area/${remoteId.sync}/`, {
            method: 'PUT',
            credentials: 'same-origin',
            headers: {'Content-Type': 'application/json'},
            body,
        }));

    }
    return remoteId.promise;

}, area => area.properties.areaLocalId);

function getIsModifiedSinceSave(area: Area) {
    const {areaLocalId} = area.properties;
    const savedArea = savedAreas[areaLocalId];

    // Do not save if they havent changed since the last time they were saved
    if (savedArea && isEqual(savedArea, area)) return false;

    // Do not save if they have already been deleted remotely
    if (savedArea && savedArea.properties.isLocallyDeleted) return false;

    return true;
}

export function getAreaRemoteId(area: Area) {
    if (!remoteIds[area.properties.areaLocalId])  {
        remoteIds[area.properties.areaLocalId] = createRemoteId(area.properties.id);
    }
    return remoteIds[area.properties.areaLocalId].promise;
}

export function registerSavedArea(area: Area) {
    savedAreas[area.properties.areaLocalId] = area;
}
